import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectPageSize } from '../redux/features/context/contextSlice';

const PAGE_SIZE_STEPS = [1, 2, 3, 4, 6, 8];

const MIN_CARD_PX = 200;

function getGridGeometry(width, height) {
  if (width < 600) return { cols: 1, maxRows: 2 };
  const aspect = width / height;
  if (aspect >= 1.7) return { cols: 4, maxRows: 2 };
  if (aspect >= 1.3) return { cols: 3, maxRows: 2 };
  if (aspect >= 0.8) return { cols: 2, maxRows: 2 };
  // Portrait
  return { cols: 1, maxRows: 2 };
}

function getArrangement(n, width, height) {
  const { cols } = getGridGeometry(width, height);
  if (n <= cols) return { cols: n, rows: 1 };
  return { cols, rows: Math.ceil(n / cols) };
}

function getOfferedPageSizes(width, height) {
  const { cols, maxRows } = getGridGeometry(width, height);
  return PAGE_SIZE_STEPS.filter(
    (n) => n <= cols || (n % cols === 0 && n / cols <= maxRows),
  );
}

function snapToOffered(n, offered) {
  return offered.reduce(
    (best, o) => (Math.abs(o - n) < Math.abs(best - n) ? o : best),
    offered[0],
  );
}

function getAutoSize(width, height) {
  const offered = getOfferedPageSizes(width, height);
  for (let i = offered.length - 1; i >= 0; i -= 1) {
    const n = offered[i];
    const { cols, rows } = getArrangement(n, width, height);
    const cardSize = Math.min(width / cols, height / rows);
    if (cardSize >= MIN_CARD_PX) return n;
  }
  return offered[0] ?? 1;
}

function useViewport() {
  const [size, setSize] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

function resolvePageSize(manualPageSize, w, h) {
  const offered = getOfferedPageSizes(w, h);
  if (manualPageSize == null) return getAutoSize(w, h);
  if (offered.includes(manualPageSize)) return manualPageSize;
  return snapToOffered(manualPageSize, offered);
}

// Ceiling on a card's share of the grid height.
function getRowCap(rows) {
  return `${Math.min(50, 100 / rows)}%`;
}

export default function useGridLayout() {
  const { w, h } = useViewport();
  const manualPageSize = useSelector(selectPageSize);
  return useMemo(() => {
    const pageSizeOptions = getOfferedPageSizes(w, h);
    const pageSize = resolvePageSize(manualPageSize, w, h);
    const arrangement = getArrangement(pageSize, w, h);
    return {
      pageSize, pageSizeOptions, arrangement, rowCap: getRowCap(arrangement.rows),
    };
  }, [manualPageSize, w, h]);
}
